<template>
  <div class="target-detail-sections">
    <div class="target-detail-header">
      <BLayout class="top-content">
        <div class="block-content">
          <VWait
            class="title-container"
            for="none"
            :visible="$wait.is('getCallTargetWait')"
          >
            <h3 class="title">
              {{ `${companyName}（${targetName}）` }}
            </h3>
            <div v-if="companyInfo?.companyName">
              <RouterLink :to="{ name: 'AccountDetail', params: { key: companyInfo.companyName } }">
                {{ companyName }}
              </RouterLink>
            </div>
            <template #waiting>
              <div>-</div>
              <div>-</div>
            </template>
          </VWait>
        </div>
        <div class="more-menu">
          <BMoreMenu left>
            <template #default>
              <BBtn
                flat
                fit
                position="left"
                size="small"
                @click="deleteCallTarget(callTargetId)"
              >
                {{ $t('general.delete.to', {item: $t('callTarget.title')}) }}
              </BBtn>
              <BBtn
                flat
                fit
                position="left"
                size="small"
                @click="duplicateCallTarget(callTargetId)"
              >
                {{ $t('general.duplicate.to', {item: $t('callTarget.title')}) }}
              </BBtn>
            </template>
          </BMoreMenu>
        </div>
      </BLayout>
    </div>
    <BDivider />
    <div class="target-detail-content">
      <BLayout
        v-if="isAvailableSequenceFeature"
        class="top-content"
      >
        <SequenceContainer
          :active-sequence-instance="activeSequenceInstance"
          @update:sequence-status="handleUpdateSequenceStatus"
          @click:sequence-apply="handleClickSequenceApply"
          @click:sequence-widget-open="handleSequenceWidgetOpen"
        />
      </BLayout>
      <BDivider
        v-if="isAvailableSequenceFeature"
        class="no-section-spacing mb-300"
      />
      <BExpansionPanel
        class="unsubscribe"
      >
        <template #header>
          <div class="block-title">
            <div class="title-text mr-100">
              {{ $t('unsubscribe.title') }}
            </div>
          </div>
        </template>
        <template #additional-header-content>
          <div class="block-title">
            <BHelp
              :content="$t('unsubscribe.description')"
            />
            <UnsubscribeMailTitle
              :unsubscribe-mail="unsubscribeMail"
            />
          </div>
        </template>
        <div class="block-content">
          <VWait
            class="full-width"
            for="none"
            :visible="$wait.is('getCallTargetWait')"
          >
            <template #waiting>
              <BContentLoader
                :height="32"
                :width="230"
              >
                <rect
                  x="0"
                  y="8.5"
                  rx="6"
                  ry="6"
                  width="160"
                  height="12"
                />
              </BContentLoader>
            </template>
            <UnsubscribeMailForm
              :call-target-email="callTargetInfo?.email"
              :unsubscribe-mail="unsubscribeMail"
              @unsubscribe:direct-mail="handleUnsubscribeDirectMail"
              @unsubscribe:bulk-mail="handleUnsubscribeBulkMail"
              @subscribe:direct-mail="handleSubscribeDirectMail"
              @subscribe:bulk-mail="handleSubscribeBulkMail"
            />
          </VWait>
        </div>
      </BExpansionPanel>
      <BDivider class="no-section-spacing mb-300" />
      <BExpansionPanel
        expand
      >
        <template #header>
          <div class="block-title">
            <div class="title-text mr-100">
              {{ $t('leadStage.title') }}
            </div>
          </div>
        </template>
        <div class="block-content">
          <VWait
            class="full-width"
            for="none"
            :visible="$wait.is('getCallTargetWait')"
          >
            <template #waiting>
              <BContentLoader
                :height="32"
                :width="230"
              >
                <rect
                  x="0"
                  y="8.5"
                  rx="6"
                  ry="6"
                  width="160"
                  height="12"
                />
              </BContentLoader>
            </template>
            <BLayout wrap>
              <LeadStageForm
                :call-target-id="callTargetId"
                :lead-stage="callTargetLeadStage"
                :lead-stages="leadStages"
                :loading="callTargetLoading"
                @change="handleChange"
              />
            </BLayout>
          </VWait>
        </div>
      </BExpansionPanel>
      <ProductCategoryAssociationSection
        v-if="isAvailableProductManagementFeature"
        :call-target-id="callTargetId"
      />
      <BExpansionPanel
        expand
      >
        <template #header>
          <div class="block-title">
            <div class="title-text mr-100">
              {{ $t('operatorAssign.assignedOperator') }}
            </div>
          </div>
        </template>
        <div class="block-content">
          <VWait
            class="full-width"
            for="none"
            :visible="operatorLoading"
          >
            <div
              fat
              :class="isExistOperator(operator) ? 'set-operator-btn' : 'set-operator-round-btn'"
              :fit="true"
              size="default"
            >
              <UserPreview
                v-if="isExistOperator(operator)"
                :user="operator"
                @click.stop="openSetOperatorModal"
              />
              <BEmptyBox
                v-else
                @click.stop="openSetOperatorModal"
              >
                <BIcon size="small">
                  add
                </BIcon>
              </BEmptyBox>
            </div>
            <template #waiting>
              <BContentLoader
                :height="24"
                :width="230"
              >
                <rect
                  x="0"
                  y="8.5"
                  rx="6"
                  ry="6"
                  width="160"
                  height="12"
                />
              </BContentLoader>
            </template>
          </VWait>
        </div>
      </BExpansionPanel>
      <BModal
        class="no-section-spacing"
        modal-name="setOperatorInDetail"
        width="380px"
        height="100px"
      >
        <SetOperator
          :current-operator-id="operator.id"
          :operator-users="activeUsers"
          :needs-withdraw-option="true"
          @assign-operator="assignOperator"
          @withdraw-operator="withdrawOperator"
        />
      </BModal>
      <BExpansionPanel
        expand
      >
        <template #header>
          <div class="block-title">
            <div class="title-text mr-100">
              {{ $t('leadSource.title') }}
            </div>
          </div>
        </template>
        <template #additional-header-content>
          <div class="block-title">
            <LeadSourceForm
              :candidates="leadSourceCandidates"
              :selected-ids="callTargetLeadSources.map((c) => c.id)"
              :call-target-id="callTargetId"
              :loading="leadSourceLoading"
              @change="handleChange"
            />
          </div>
        </template>
        <div class="block-content">
          <VWait
            class="full-width"
            for="none"
            :visible="leadSourceLoading"
          >
            <template #waiting>
              <BContentLoader
                :height="32"
                :width="230"
              >
                <rect
                  x="0"
                  y="8.5"
                  rx="6"
                  ry="6"
                  width="160"
                  height="12"
                />
              </BContentLoader>
            </template>
            <BLayout wrap>
              <LeadSourcePreview
                v-for="leadSource in callTargetLeadSources"
                :key="`lead-source-${leadSource.id}`"
                class="mr-100 mb-100"
                :lead-source="leadSource"
              />
            </BLayout>
          </VWait>
        </div>
      </BExpansionPanel>
      <BExpansionPanel
        v-if="salesforceCampaigns.length > 0"
        expand
      >
        <template #header>
          <div class="block-title">
            <div class="title-text mr-100">
              {{ $t('salesforceCampaign.title') }}
            </div>
          </div>
        </template>
        <div class="block-content">
          <VWait
            class="full-width"
            for="none"
            :visible="$wait.is('getCallTargetWait')"
          >
            <template #waiting>
              <BContentLoader
                :height="32"
                :width="230"
              >
                <rect
                  x="0"
                  y="8.5"
                  rx="6"
                  ry="6"
                  width="160"
                  height="12"
                />
              </BContentLoader>
            </template>
            <BLayout wrap>
              <BLabel
                v-for="salesforceCampaign in dispSalesforceCampaigns"
                :key="`salesforce-campaign-${salesforceCampaign.id}`"
                class="mr-100 mb-100"
                round
              >
                <span>{{ salesforceCampaign.name }}</span>
              </BLabel>
            </BLayout>
          </VWait>
          <div
            v-if="salesforceCampaigns.length > 10 && !dispAllSalesforceCampaign"
            class="see-more"
            @click="seeMoreCampaign"
          >
            <div class="block-text-color">
              {{ $t('general.seeMore') }}
            </div>
            <BIcon>
              arrow_drop_down
            </BIcon>
          </div>
        </div>
      </BExpansionPanel>
      <BExpansionPanel
        expand
      >
        <template #header>
          <div class="block-title">
            <div class="title-text mr-100">
              {{ $t('freeTag.title') }}
            </div>
          </div>
        </template>
        <template #additional-header-content>
          <div class="block-title">
            <FreeTagForm
              :candidates="freeTagCandidates"
              :selected-ids="callTargetFreeTags.map((c) => c.id)"
              :call-target-id="callTargetId"
              :loading="freeTagLoading"
              @change="handleChange"
            />
          </div>
        </template>
        <div class="block-content">
          <VWait
            class="full-width"
            for="none"
            :visible="freeTagLoading"
          >
            <template #waiting>
              <BContentLoader
                :height="32"
                :width="230"
              >
                <rect
                  x="0"
                  y="8.5"
                  rx="6"
                  ry="6"
                  width="160"
                  height="12"
                />
              </BContentLoader>
            </template>
            <BFreeTag
              v-for="freeTag in callTargetFreeTags"
              :key="`freeTag-${freeTag.id}`"
              class="mr-100 mb-100"
              :tag="freeTag"
            />
          </VWait>
        </div>
      </BExpansionPanel>
      <div class="profile-area">
        <BExpansionPanel
          expand
        >
          <template #header>
            <div class="block-title">
              <div class="title-text">
                {{ $t('companyInfo.title') }}
              </div>
            </div>
          </template>
          <div class="block-content">
            <LeadDetailListItem
              v-for="(item, index) in companyInfoAttributes"
              :key="index"
              v-model:editing-node="editingNode"
              class="mb-200"
              :title="item.title"
              :value="item.value"
              :node="item.node"
              :rules="item.rules"
              :loading="callTargetLoading"
              :items="item.items"
              @save="editSaveCompanyInfo"
            >
              <template
                v-if="item.node === 'companyName'"
                #help
              >
                <BTooltip
                  :key="`tooltip1-${item.node}-${item.value}`"
                  top
                >
                  <a
                    v-if="companyInfo.companyName"
                    :href="searchCompanyName"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <BBtn
                      :key="`btn1-${item.node}-${item.value}`"
                      size="mini"
                      fab
                    >
                      <BIcon
                        :key="`icon1-${item.node}-${item.value}`"
                        size="small"
                      >public</BIcon>
                    </BBtn>
                  </a>
                  <template #content>
                    <span>{{ $t('callTarget.search.companyName') }}</span>
                  </template>
                </BTooltip>
              </template>
              <template
                v-else-if="item.node === 'companyUrl' && companyInfo.companyUrl"
                #help
              >
                <BLinkBtn :value="companyInfo.companyUrl" />
              </template>
              <template
                v-else-if="item.node === 'companyTel_1'"
                #help
              >
                <CallButtonContainer
                  :phone-number="companyInfo.companyTel1"
                  :sf-sync-setting-info="sfSyncSettingInfo"
                  phone-number-key="company_tel_1"
                />
              </template>
              <template
                v-else-if="
                  item.node === 'companyAddress' && companyAddress != ''
                "
                #help
              >
                <BTooltip top>
                  <a
                    :href="searchCompanyAddress"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <BBtn
                      size="mini"
                      fab
                    >
                      <BIcon size="small">location_on</BIcon>
                    </BBtn>
                  </a>
                  <template #content>
                    <span>{{ $t('callTarget.search.address') }}</span>
                  </template>
                </BTooltip>
              </template>
            </LeadDetailListItem>
          </div>
        </BExpansionPanel>
        <BExpansionPanel
          expand
        >
          <template #header>
            <div class="block-title">
              <div class="title-text">
                {{ $t('callTargetInfo.title') }}
              </div>
            </div>
          </template>
          <div class="block-content">
            <CallTargetNameItem
              v-for="(item, index) in callTargetNameAttributes"
              :key="index"
              v-model:editing-node="editingNode"
              class="mb-200"
              :title="item.title"
              :value="item.value"
              :items="item.items"
              :node="item.node"
              :rules="item.rules"
              :loading="callTargetLoading"
              @save="editSaveCallTargetInfo"
            />
            <LeadDetailListItem
              v-for="(item, index) in callTargetInfoAttributes"
              :key="index"
              v-model:editing-node="editingNode"
              class="mb-200"
              :title="item.title"
              :value="item.value"
              :node="item.node"
              :rules="item.rules"
              :loading="callTargetLoading"
              @save="editSaveCallTargetInfo"
            >
              <template #notification>
                <template
                  v-if="
                    item.node === 'email' &&
                      !!item.notification
                  "
                >
                  <BTooltip
                    right
                    :content="$t('duplicatedList.show')"
                  >
                    <button
                      type="button"
                      class="email-notification"
                      @click="$emit('click-email-notification')"
                    >
                      {{ item.notification }}
                    </button>
                  </BTooltip>
                </template>
              </template>
              <template #help>
                <template
                  v-if="item.node === 'personalTel' && callTargetInfo.personalTel"
                >
                  <CallButtonContainer
                    :phone-number="callTargetInfo.personalTel"
                    :sf-sync-setting-info="sfSyncSettingInfo"
                    phone-number-key="personal_tel"
                  />
                </template>
                <template
                  v-if="
                    isAvailableDirectMailFeature &&
                      item.node === 'email' &&
                      item.value
                  "
                >
                  <BBtn
                    type="primary"
                    size="mini"
                    fab
                    @click="$emit('click-mail-button')"
                  >
                    <BIcon
                      size="small"
                      type="white"
                    >
                      mail
                    </BIcon>
                  </BBtn>
                </template>
              </template>
            </LeadDetailListItem>
          </div>
        </BExpansionPanel>
        <BExpansionPanel
          v-if="customFields.length > 0"
          expand
        >
          <template #header>
            <div class="block-title">
              <div class="title-text">
                {{ $t('customField.title') }}
              </div>
            </div>
          </template>
          <div class="block-content">
            <CustomFieldListItem
              v-for="field in customFields"
              :key="field.name"
              v-model:editing-node="editingNode"
              class="mb-200"
              :call-target-id="callTargetId"
              :custom-field-id="field.id"
              :options="field.options"
              :node="field.name"
              :title="field.name"
              :value="getCustomFieldValue(field)"
              :sf-link="getCustomFieldSfLinkValue(field)"
              :loading="customFieldsLoading"
              :type="field.dataType"
              @change="handleChange"
              @save="editCustomContent($event, field)"
            />
          </div>
        </BExpansionPanel>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash';
import { RouterLink } from 'vue-router';
import { mapWaitingActions } from 'vue-wait';
import { SequenceInstances } from '@/api/user/models/sequence_instances';
import CallButtonContainer from '@/components/organisms/user/lead/detail/CallButtonContainer.vue';
import CallTargetNameItem from '@/components/organisms/user/lead/detail/CallTargetNameItem.vue';
import CustomFieldListItem from '@/components/organisms/user/lead/detail/CustomFieldListItem.vue';
import FreeTagForm from '@/components/organisms/user/lead/detail/FreeTagForm.vue';
import LeadDetailListItem from '@/components/organisms/user/lead/detail/LeadDetailListItem.vue';
import LeadSourceForm from '@/components/organisms/user/lead/detail/LeadSourceForm.vue';
import LeadStageForm from '@/components/organisms/user/lead/detail/LeadStageForm.vue';
import ProductCategoryAssociationSection from '@/components/organisms/user/lead/detail/ProductCategoryAssociationSection.vue';
import SequenceContainer from '@/components/organisms/user/lead/detail/SequenceContainer.vue';
import UnsubscribeMailForm from '@/components/organisms/user/lead/detail/UnsubscribeMailForm.vue';
import UnsubscribeMailTitle from '@/components/organisms/user/lead/detail/UnsubscribeMailTitle.vue';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';

import { useLeadAttributes, useSfReferenceInfo, useCustomFieldAttributes } from '@/composable/user/lead/attributes';
import inputValidation from '@/mixins/input_validation';

export default {
  components: {
    LeadStageForm,
    ProductCategoryAssociationSection,
    LeadSourceForm,
    FreeTagForm,
    LeadDetailListItem,
    CustomFieldListItem,
    CallButtonContainer,
    CallTargetNameItem,
    UnsubscribeMailTitle,
    UnsubscribeMailForm,
    SequenceContainer,
    RouterLink,
  },
  mixins: [inputValidation],
  props: {
    callTarget: {
      type: Object,
      default() {
        return {};
      },
    },
    leadStages: {
      type: Array,
      default() {
        return [];
      },
    },
    callTargetLeadStage: {
      type: Object,
      default() {
        return {
          id: undefined,
          name: undefined,
          colorName: undefined,
          sortOrder: undefined,
          isPreset: undefined,
          createdAt: undefined,
          updatedAt: undefined,
        };
      },
    },
    companyInfo: {
      type: Object,
      default () {
        return {
          companyName: '',
          companyUrl: '',
          companyTel1: '',
          companyCountry: '',
          companyPostalCode: '',
          companyPrefecture: '',
          companyCity: '',
          companyAddressLine: '',
          email: '',
        };
      },
    },
    callTargetInfo: {
      type: Object,
      default () {
        return {
          personalTel: '',
          name: '',
          nameKana: '',
          email: '',
        };
      },
    },
    duplicatedEmailsCount: Number,
    customFields: {
      type: Array,
      default: () => [],
    },
    editable: {
      type: Boolean,
      default: true,
    },
    freeTagCandidates: Array,
    leadSourceCandidates: Array,
    callTargetFreeTags: Array,
    callTargetLeadSources: Array,
    callTargetId: Number,
    unsubscribeMail: Object,
    activeUsers: Array,
    sequenceInstances: SequenceInstances,
    salesforceCampaigns: {
      type: Array,
      default: () => [],
    },
    dispAllSalesforceCampaign: {
      type: Boolean,
      default: true,
    },
    operator: Object,
    sfSyncSettingInfo: {
      type: Object,
      required: true,
    },
  },
  emits: [
    'subscribe:direct-mail',
    'subscribe:bulk-mail',
    'unsubscribe:direct-mail',
    'unsubscribe:bulk-mail',
    'click-email-notification',
    'click-mail-button',
    'update-custom-field-value',
    'update-company-info',
    'update-call-target-info',
    'change',
    'delete-call-target',
    'duplicate-call-target',
    'update-sequence-status',
    'click:sequence-apply',
    'click-disp-sf-campaign-button',
  ],
  setup(props) {
    const { isAvailableSequenceFeature, isAvailableDirectMailFeature, isAvailableProductManagementFeature } = useAvailableFeatureCheck();
    const { companyInfoAttributes, companyAddress, callTargetNameAttributes, callTargetInfoAttributes } = useLeadAttributes(() => props.callTarget, () => props.duplicatedEmailsCount);
    const { sfReferenceInfo } = useSfReferenceInfo(() => props.callTargetId);
    const { getCustomFieldValue, getCustomFieldSfLinkValue } = useCustomFieldAttributes(() => sfReferenceInfo.value);
    return {
      isAvailableSequenceFeature,
      isAvailableDirectMailFeature,
      isAvailableProductManagementFeature,
      companyInfoAttributes,
      companyAddress,
      callTargetNameAttributes,
      callTargetInfoAttributes,
      getCustomFieldValue,
      getCustomFieldSfLinkValue,
    };
  },
  data() {
    return {
      editingNode: null,
      loadingOperator: false,
      operatorModalName: 'setOperatorInDetail',
    };
  },
  computed: {
    searchCompanyName() {
      const target = encodeURIComponent(`${this.companyInfo.companyName}`);
      return `${this.$t('general.googleSearch')}${target}`;
    },
    searchCompanyAddress() {
      const target = encodeURIComponent(`${this.companyAddress}`);
      return `${this.$t('general.googleMapSearch')}${target}`;
    },
    freeTagLoading() {
      return this.$wait.is([
        'getCallTargetFreeTagsWait',
        'deleteCallTargetFreeTagWait',
        'appendCallTargetFreeTagWait',
      ]);
    },
    operatorLoading() {
      return this.$wait.is([
        'createOperatorWait',
        'getOperatorWait',
        'withdrawOperatorWait',
      ]);
    },
    leadSourceLoading() {
      return this.$wait.is([
        'getCallTargetLeadSourcesWait',
        'deleteCallTargetLeadSourceWait',
        'appendCallTargetLeadSourceWait',
      ]);
    },
    callTargetLoading() {
      return this.$wait.is('updateCallTargetWait');
    },
    customFieldsLoading() {
      return this.$wait.is('updateCustomFieldValueWait');
    },
    companyName() {
      if (this.companyInfo == null) return '-';
      return this.companyInfo.companyName;
    },
    targetName() {
      if (this.callTargetInfo == null) return '-';
      return this.callTargetInfo.name || this.$t('callTarget.nameless');
    },
    activeSequenceInstance() {
      return this.sequenceInstances?.findActive();
    },
    dispSalesforceCampaigns() {
      if (this.dispAllSalesforceCampaign) return this.salesforceCampaigns;
      return this.salesforceCampaigns.slice(0, 10);
    },
  },
  methods: {
    ...mapWaitingActions('userUi', {
      setTargetHeaderMoveButtonDisabledAction:
        'setTargetHeaderMoveButtonDisabledWait',
      setSelectedSequenceInstanceIdAction:
        'setSelectedSequenceInstanceIdWait',
      setTargetSequenceInstanceFormVisibleAction:
        'setTargetSequenceInstanceFormVisibleWait',
    }),
    ...mapWaitingActions('user', {
      getCallTargetsBackgroundAction: { action: 'getCallTargetsAction', loader: 'getCallTargetsBackgroundWait' },
      createOperatorAction: 'createOperatorWait',

      getOperatorAction: 'getOperatorWait',
      withdrawOperatorAction: 'withdrawOperatorWait',
    }),
    editCustomContent({ event, node }, customField) {
      if (this.$wait.is('updateCustomFieldValuesWait')) {
        alert(this.$t('callTarget.waitingUpdate'));
        return;
      }
      this.setTargetHeaderMoveButtonDisabledAction(true);
      const newCustomField = lodash.cloneDeep(customField);
      newCustomField.value = event;
      this.$emit('update-custom-field-value', newCustomField);
    },
    editSaveCompanyInfo(callTarget) {
      this.setTargetHeaderMoveButtonDisabledAction(true);
      if (this.$wait.is('updateCallTargetWait')) {
        alert(this.$t('callTarget.waitingUpdate'));
        return;
      }
      this.$emit('update-company-info', callTarget);
    },
    openSetOperatorModal () {
      this.$modal.show(this.operatorModalName);
    },
    async assignOperator (selectedOperatorId) {
      try {
        await this.createOperatorAction({
          body: {
            operatorId: selectedOperatorId,
            callTargetId: this.callTargetId,
          },
        });
        await this.getOperatorAction(this.callTargetId);
        this.$toast.show(this.$t('general.done.of', { action: this.$t('operatorAssign.of', { item: this.$t('home.member') }) }));
      } catch (e) {
        this.$toast.show({
          message: this.$t('general.fail.to', { action: this.$t('operatorAssign.assign') }),
          type: 'error',
        });
      } finally {
        this.$modal.hide(this.operatorModalName);
      }
      this.getCallTargetsBackground();
    },
    async withdrawOperator () {
      try {
        await this.withdrawOperatorAction(this.callTargetId);
        this.$toast.show(this.$t('general.done.of', { action: `${this.$t('operatorAssign.assign')}${this.$t('general.withdraw.text')}` }));
      } catch (e) {
        this.$toast.show({
          message: this.$t('general.fail.to', { action: `${this.$t('operatorAssign.assign')}${this.$t('general.withdraw.text')}` }),
          type: 'error',
        });
      } finally {
        this.$modal.hide(this.operatorModalName);
      }
      this.getCallTargetsBackground();
    },
    async getCallTargetsBackground () {
      this.$toast.show({ message: this.$t('callList.update.loading'), type: 'info' });
      await this.getCallTargetsBackgroundAction();
      this.$toast.show({ message: this.$t('callList.update.done'), type: 'success' });
    },
    editSaveCallTargetInfo(callTarget) {
      this.setTargetHeaderMoveButtonDisabledAction(true);
      if (this.$wait.is('updateCallTargetWait')) {
        alert(this.$t('callTarget.waitingUpdate'));
        return;
      }
      this.$emit('update-call-target-info', callTarget);
    },
    handleChange() {
      this.$emit('change');
    },
    deleteCallTarget(callTargetId) {
      this.$emit('delete-call-target', callTargetId);
    },
    duplicateCallTarget(callTargetId) {
      this.$emit('duplicate-call-target', callTargetId);
    },
    isExistOperator (operator) {
      return operator?.id != null;
    },
    handleSequenceWidgetOpen() {
      this.setSelectedSequenceInstanceIdAction(this.activeSequenceInstance.id);
      this.setTargetSequenceInstanceFormVisibleAction(true);
    },
    handleUpdateSequenceStatus(action) {
      this.$emit('update-sequence-status', action, this.activeSequenceInstance.id);
    },
    handleClickSequenceApply() {
      this.$emit('click:sequence-apply');
    },
    handleUnsubscribeDirectMail() {
      this.$emit('unsubscribe:direct-mail', this.callTargetInfo?.email);
    },
    handleUnsubscribeBulkMail() {
      this.$emit('unsubscribe:bulk-mail', this.callTargetId);
    },
    handleSubscribeDirectMail() {
      this.$emit('subscribe:direct-mail', this.callTargetInfo?.email);
    },
    handleSubscribeBulkMail() {
      this.$emit('subscribe:bulk-mail', this.callTargetId);
    },
    seeMoreCampaign() {
      this.$emit('click-disp-sf-campaign-button');
    },
  },
};
</script>

<style lang="scss" scoped>
.see-more  {
  display: flex;
  float: right;
  .block-text-color {
    color: $basecolor-primary;
    font-size: $fontsize-100;
  }
}
// .no-section-spacing: セクション共通の padding/margin を適用しない要素に付与するマーカークラス
// BDivider や BModal など、セクション間のスペーシングが不要な要素に使用する
.target-detail-sections {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.target-detail-header {
  flex-shrink: 0;
  padding-block: $basespace-300;
  padding-inline: $basespace-400 30px;
}

.target-detail-content {
  flex-grow: 1;
  min-height: 0;
  overflow-y: scroll;
  padding-block: $basespace-300;

  > *:not(.no-section-spacing) {
    padding: 0 $basespace-400;

    &:not(:last-child) {
      margin-bottom: $basespace-400;
    }
  }

  > .unsubscribe {
    margin-bottom: $basespace-400;
  }

  > .profile-area > * {
    &:not(:last-child) {
      margin-bottom: $basespace-400;
    }
  }
}

.block-title {
  display: flex;
  align-items: center;
  width: 100%;
  font-weight: 600;
}

.set-operator-btn {
  width: 200px;
  height: 42px;
  &:hover {
    cursor: pointer;
    background: #F6F6F6;
    border-radius: 6px;
  }
}

.set-operator-round-btn {
  border: none ;
  &:hover {
    border-radius: 50%;
  }
  .b-empty-box {
    border-radius: 50%;
    width: 36px;
    height: 36px;
    padding: 9px;
    &:hover {
      background-color: #F6F6F6;
    }
    .b-icon {
      font-size: 16px;
    }
  }
}

.list-title {
  position: relative;
  width: 90px;
  min-width: 90px;
  max-width: 90px;
  background-color: $bgcolor-dark;
  padding: 4px $basespace-200;
}

.list-content {
  position: relative;
  width: 100%;
  word-wrap: break-word;
  word-break: break-all;
}

.title-container {
  width: 100%;
  padding-right: $basespace-100;
  .title {
    padding-bottom: calc($basespace-100 - 2px);
  }
}

.full-width {
  width: 100%;
}

.top-content {
  justify-content: space-between;
  align-items: center;
}

.email-notification {
  color: $textcolor-white;
  background-color: $basecolor-error;
  height: 18px;
  border-radius: 20px;
  width: 38px;
}
</style>
